import { useCallback, useEffect, useMemo, useState } from 'react';
import ReactFlow, {
    MiniMap,
    Controls,
    Background,
    useReactFlow,
    applyEdgeChanges,
} from 'react-flow-renderer';
import { withNamespaces } from 'react-i18next';
import { Button, Modal, ModalBody, ModalHeader } from 'reactstrap';

import CompanyNode from './components/node-types/CompanyNode';
import PersonNode from './components/node-types/PersonNode';
import DepartmentNode from './components/node-types/DepartmentNode';
import GroupNode from './components/node-types/GroupNode';
import useSafeNodesChange from './hooks/useSafeNodesChange';
import { createGraphElements } from './utils/graphLayout';

import './styles.scss';

const OwnershipGraphReactFlow = ({
    t,
    data,
    height = 800,
}) => {
    const [nodes, setNodes] = useState([]);
    const [edges, setEdges] = useState([]);

    const onNodesChange = useSafeNodesChange(setNodes);

    const [initialNodes, initialEdges] = useMemo(() => {
        if (!data) return [[], []];

        return createGraphElements(data.entities, data.relations, data.from_cin, { t });
    }, [data, t]);

    const [modalOpen, setModalOpen] = useState(false);
    const [selectedNodeInfo, setSelectedNodeInfo] = useState(null);

    const toggleModal = () => setModalOpen(!modalOpen);

    const reactFlowInstance = useReactFlow();

    const handleFitView = useCallback(() => {
        reactFlowInstance.fitView({ padding: 0.2 });
    }, [reactFlowInstance]);

    const onEdgesChange = useCallback(
        (changes) => setEdges((eds) => applyEdgeChanges(changes, eds)),
        []
    );

    const onNodeClick = useCallback((event, node) => {
        setSelectedNodeInfo(node.data || {});
        setModalOpen(true);
    }, []);

    useEffect(() => {
        setNodes(initialNodes);
        setEdges(initialEdges);
    }, [initialNodes, initialEdges]);

    return (
        <div
            style={{
                height,
                width: '100%',
                position: 'relative',
                overflow: 'hidden',
            }}
        >
            <Button
                color="secondary"
                onClick={handleFitView}
                style={{
                    position: 'absolute',
                    zIndex: 10,
                    right: 10,
                    top: 10,
                    padding: '6px 12px',
                }}
            >
                {t('Fit View')}
            </Button>

            <ReactFlow
                nodes={nodes}
                edges={edges}
                nodesConnectable={false}
                nodesDraggable
                elementsSelectable
                onNodesChange={onNodesChange}
                onEdgesChange={onEdgesChange}
                onNodeClick={onNodeClick}
                attributionPosition="bottom-left"
                nodeTypes={{
                    company: CompanyNode,
                    person: PersonNode,
                    department: DepartmentNode,
                    group: GroupNode,
                }}
            >
                <MiniMap
                    nodeStrokeColor={(n) => (n.style?.backgroundColor ? n.style.backgroundColor : '#eee')}
                    nodeColor={(n) => (n.style?.backgroundColor ? n.style.backgroundColor : '#eee')}
                />
                <Controls />
                <Background color="#aaa" gap={16} />
            </ReactFlow>

            <Modal isOpen={modalOpen} toggle={toggleModal}>
                <ModalHeader toggle={toggleModal}>
                    {selectedNodeInfo?.label || t('Entity Info')}
                </ModalHeader>
                <ModalBody>
                    {selectedNodeInfo ? (
                        <div>
                            <p>
                                <strong>{t('Address')}:</strong>
                                {selectedNodeInfo?.additional_information?.address_1}
                            </p>
                            <p>
                                <strong>{t('City')}:</strong>
                                {selectedNodeInfo?.additional_information?.city}
                            </p>
                            <p>
                                <strong>{t('Country')}:</strong>
                                {selectedNodeInfo?.additional_information?.country}
                            </p>
                            <p>
                                <strong>{t('Postcode')}:</strong>
                                {selectedNodeInfo?.additional_information?.postcode}
                            </p>
                        </div>
                    ) : (
                        <p>{t('No additional info available.')}</p>
                    )}
                </ModalBody>
            </Modal>
        </div>
    );
};

export default withNamespaces()(OwnershipGraphReactFlow);
